import { isFunction } from './is-function';
import { mimeTypes } from 'mime-wrapper';

const MIME_BY_EXT: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
};

/** Chrome/macOS may hand .xlsx to Excel before applying link.download – generic MIME avoids that. */
const EXCEL_DOWNLOAD_MIME = 'application/octet-stream';

export function resolveDownloadFileName(documentTitle: string, fileExt: string): string {
    const normalizedTitle = documentTitle
        .replace(/\.xlsx$/i, '')
        .replace(/_+(?=\d{8}(?:\.\w+)?$)/, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

    return normalizedTitle.includes(`.${fileExt}`)
        ? normalizedTitle
        : `${normalizedTitle}.${fileExt}`;
}

export function resolveDownloadMimeType(fileExt: string, mimeType?: string, sourceBlob?: Blob): string {
    if (fileExt.toLowerCase() === 'xlsx') {
        return EXCEL_DOWNLOAD_MIME;
    }

    return mimeType || MIME_BY_EXT[fileExt.toLowerCase()] || sourceBlob?.type || EXCEL_DOWNLOAD_MIME;
}

/**
 * Trigger a file download via a temporary object URL.
 * Revoke is delayed (not on window focus) so Chrome's PDF viewer / download bar
 * does not lose the blob while still displaying it – that caused ERR_FILE_NOT_FOUND
 * and in some cases replaced the SPA tab with a dead blob: URL.
 */
export function downloadFile(file: any, documentTitle: string, fileExt?: string, mimeType?: string): void {

    if (isFunction(navigator['msSaveOrOpen']) || isFunction(navigator['msSave'])) {
        throw Error(`Download nicht möglich! Der Internet Explorer ist sehr veraltet und wird von uns nicht unterstützt.`);
    }

    if (!documentTitle) {
        throw Error(`Download nicht möglich, Dateinamen nicht angegeben.`);
    }

    if (!fileExt) {
        fileExt = mimeTypes.getExtension(mimeType);
    }

    if (!fileExt && documentTitle.includes('.')) {
        const documentTitleParts = documentTitle.split('.');
        fileExt = documentTitleParts[documentTitleParts.length - 1];
    }

    if (!fileExt) {
        throw Error(`Download nicht möglich, Dateiendung nicht angegeben.`);
    }

    const fileName = resolveDownloadFileName(documentTitle, fileExt);

    const resolvedMime = resolveDownloadMimeType(fileExt, mimeType, file instanceof Blob ? file : undefined);
    const blobSource = new Blob([file instanceof Blob ? file : file], { type: resolvedMime });
    const blob = typeof File !== 'undefined'
        ? new File([blobSource], fileName, { type: resolvedMime })
        : blobSource;

    const objectUrl = URL.createObjectURL(blob);
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';
    link.rel = 'noopener';

    // some browser needs the anchor to be in the doc
    document.body.append(link);
    link.click();

    // Delay cleanup: immediate remove/revoke can make Chrome navigate the current tab
    // to the blob URL (replacing the Angular app) or break an open PDF preview.
    window.setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(objectUrl);
    }, 60_000);
}
